import React, { useCallback, useState, useMemo, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Formik, Form as FormikForm } from 'formik'
import { MdFilterList, MdDelete, MdKeyboardArrowDown } from 'react-icons/md'
import * as reducks from '../../../reducks'
import Tag from '../../commons/Tag/Tag'
import Flex from '../../commons/Flex/Flex'
import Text from '../../commons/Text/Text'
import Modal from '../../commons/Modal/Modal'
import Button from '../../commons/Button/Button'
import ModalMatriz from './ModalMatrizTimelineFilters'
import DatePickerFieldFormik from '../../commons/Fields/DatePickerFieldFormik'
import SelectWithSearchFieldFormik from '../../commons/Fields/SelectWithSearchFieldFormik'
import styles from './timelineContainer.module.scss'

const INITIAL_VALUES = {
  period: '',
  store_tab_numbers: null,
  partner_std_code: null,
  indicators: []
}

const TimelineCommonFilters = () => {
  let resetFormRef
  let valuesRef

  const filters = useSelector((state) => state.timelineReducer.filters)
  const wallets = useSelector((state) => state.walletReducer.wallets)
  const partnerStdCodes = useSelector(
    (state) => state.filtersReducer.partnerStdCodes
  )
  const walletLoading = useSelector((state) => state.walletReducer.fetching)

  const hasFilters =
    filters.period || filters.store_tab_numbers || filters.partner_std_code
  const hasPriorityMatricesFilter = filters?.indicators?.length > 0

  const tagIcon = useCallback(
    () => <MdFilterList color={hasFilters ? 'white' : '#737373'} />,
    [hasFilters]
  )

  const [open, toggleModal] = useState(false)
  const [showModalMatrizFilter, setShowMatrizFilter] = useState(false)

  const openModal = useCallback(() => toggleModal(true))
  const closeModal = useCallback(() => toggleModal(false))

  const handleClear = useCallback(() => {
    if (!resetFormRef) return

    const newValues = {}

    Object.keys(valuesRef).forEach((field) => {
      newValues[field] = INITIAL_VALUES[field]
    })

    resetFormRef({ values: newValues })
  }, [resetFormRef, valuesRef])

  const modalHeader = useCallback(() => (
    <>
      <Text value="Filtros" size="md" color="gray" variation="dark" bold />

      <button type="button" onClick={handleClear}>
        <Text
          value="Limpar"
          color="gray"
          variation="dark"
          size="xxs"
          margin="0px 5px 0px 0px"
        />
        <MdDelete color="#4D4D4D" size={15} />
      </button>
    </>
  ))

  const dispatch = useDispatch()

  const handleSubmit = useCallback(async (data) => {
    await dispatch(
      reducks.resources.timeline.actions.receiveFiltersAndGetTimeline(data)
    )

    closeModal()
  })

  const extractFilters = useCallback(
    () => ({
      period: filters.period || INITIAL_VALUES.period,
      store_tab_numbers:
        filters.store_tab_numbers || INITIAL_VALUES.store_tab_numbers,
      partner_std_code:
        filters.partner_std_code || INITIAL_VALUES.partner_std_code,
      indicators: filters.indicators || INITIAL_VALUES.indicators
    }),
    [filters]
  )

  const initialValues = useMemo(() => extractFilters(), [filters])

  useEffect(() => {
    dispatch(reducks.resources.filters.actions.getPartners())
    resetFormRef({
      values: extractFilters()
    })
  }, [open])

  return (
    <div className={styles.containerFlex}>
      <Tag
        white
        width="114px"
        text="Filtros"
        active={hasFilters}
        Icon={tagIcon}
        onClick={openModal}
      />

      <Tag
        white
        text="Matriz de Prioridades"
        active={hasPriorityMatricesFilter}
        Icon={() => (
          <MdKeyboardArrowDown
            color={hasPriorityMatricesFilter ? 'white' : '#4D4D4D'}
          />
        )}
        onClick={() => setShowMatrizFilter(true)}
      />

      <div>
        <button
          type="button"
          className={styles.buttonText}
          onClick={() => {
            dispatch(
              reducks.resources.timeline.actions.resetFiltersAndGetTimeline()
            )
          }}
        >
          <Text
            value="Limpar"
            color="gray"
            variation="dark"
            size="xs"
            font="custom"
          />
          <MdDelete color="#4D4D4D" />
        </button>
      </div>

      <ModalMatriz
        visible={showModalMatrizFilter}
        closeCallback={() => setShowMatrizFilter(false)}
      />

      <Modal
        rounded
        visible={open}
        size="small"
        onClickOverlay={closeModal}
        HeaderContent={modalHeader}
      >
        <Formik
          enableReinitialize
          initialValues={initialValues}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, isValid, values, resetForm, setFieldValue }) => {
            const disabled = isSubmitting || !isValid

            resetFormRef = resetForm
            valuesRef = values

            return (
              <FormikForm>
                <Flex
                  justifyContent="space-between"
                  alignItems="center"
                  margin="40px 0px 24px"
                >
                  <Text
                    value="Período visível"
                    size="sm"
                    color="gray"
                    variation="dark"
                    font="custom"
                    bold
                  />

                  <DatePickerFieldFormik name="period" />
                </Flex>

                <Flex column>
                  <Flex
                    alignItems="center"
                    justifyContent="space-between"
                    margin="0px 0px 10px 0px"
                  >
                    <Text
                      value="Cliente"
                      size="sm"
                      fontWeight="600"
                      color="gray"
                      variation="dark"
                    />

                    <Text
                      value="Limpar"
                      color="gray"
                      variation="dark"
                      size="xxs"
                      underline
                      onClick={() => setFieldValue('store_tab_numbers', null)}
                    />
                  </Flex>

                  <SelectWithSearchFieldFormik
                    name="store_tab_numbers"
                    placeholder="Selecione um cliente"
                    options={wallets.map((item) => ({
                      value: item.tabNumber,
                      name: item.nameFantasy
                    }))}
                    loading={walletLoading}
                    onSearch={(search) => {
                      if (search.length > 2) {
                        dispatch(
                          reducks.resources.wallet.actions.walletApplyFilters({
                            search,
                            page: undefined,
                            perPage: undefined
                          })
                        )
                      }
                    }}
                  />
                </Flex>

                <Flex column margin="30px 0px">
                  <Flex
                    alignItems="center"
                    justifyContent="space-between"
                    margin="0px 0px 10px 0px"
                  >
                    <Text
                      value="Nome do consultor ou subconsultor"
                      size="sm"
                      fontWeight="600"
                      color="gray"
                      variation="dark"
                    />

                    <Text
                      value="Limpar"
                      color="gray"
                      variation="dark"
                      size="xxs"
                      underline
                      onClick={() => setFieldValue('partner_std_code', null)}
                    />
                  </Flex>

                  <SelectWithSearchFieldFormik
                    name="partner_std_code"
                    placeholder="Selecione o consultor ou subconsultor"
                    optionFilterProp="children"
                    filterOption
                    options={partnerStdCodes.content
                      .asMutable()
                      .map((item) => ({
                        value: item.id,
                        name: item.name
                      }))}
                    loading={partnerStdCodes.fetching}
                  />
                </Flex>

                <Flex
                  alignItems="center"
                  justifyContent="center"
                  margin="30px 0px"
                >
                  <Button
                    type="secondary"
                    buttonType="submit"
                    disabled={disabled}
                  >
                    Aplicar Filtro
                  </Button>
                </Flex>
              </FormikForm>
            )
          }}
        </Formik>
      </Modal>
    </div>
  )
}

export default TimelineCommonFilters
